/**
 * API Client for Music Event Platform
 *
 * Features:
 * - Auto-includes Bearer token from auth context
 * - Auto-refreshes token on 401
 * - Consistent error handling
 * - Type-safe responses
 */

// =====================
// Types
// =====================

export interface ApiError {
	message: string;
	code?: string;
	field?: string;
}

export interface ApiResponse<T = unknown> {
	data?: T;
	error?: ApiError;
	status: number;
}

export interface PaginationParams {
	page?: number;
	limit?: number;
	sort?: string;
	order?: "asc" | "desc";
}

export interface PaginatedResponse<T> {
	data: T[];
	total: number;
	page: number;
	limit: number;
	totalPages: number;
}

// =====================
// Token Storage
// =====================

const TOKEN_KEY = "auth_token";
const REFRESH_TOKEN_KEY = "refresh_token";

export const tokenStorage = {
	getAccessToken: (): string | null => {
		return localStorage.getItem(TOKEN_KEY);
	},

	setAccessToken: (token: string): void => {
		localStorage.setItem(TOKEN_KEY, token);
	},

	getRefreshToken: (): string | null => {
		return localStorage.getItem(REFRESH_TOKEN_KEY);
	},

	setRefreshToken: (token: string): void => {
		localStorage.setItem(REFRESH_TOKEN_KEY, token);
	},

	clearTokens: (): void => {
		localStorage.removeItem(TOKEN_KEY);
		localStorage.removeItem(REFRESH_TOKEN_KEY);
		// Notify listeners that auth state has changed
		window.dispatchEvent(new CustomEvent('auth:cleared'));
	},
};

// =====================
// API Client
// =====================

class ApiClient {
	private baseUrl: string;
	private refreshPromise: Promise<string | null> | null = null;

	constructor(baseUrl: string = "/api") {
		this.baseUrl = baseUrl;
	}

	/**
	 * Set base URL for API calls
	 */
	setBaseUrl(url: string): void {
		this.baseUrl = url;
	}

	/**
	 * Get headers for requests
	 */
	private getHeaders(): Record<string, string> {
		const headers: Record<string, string> = {
			"Content-Type": "application/json",
		};

		const token = tokenStorage.getAccessToken();
		if (token) {
			headers["Authorization"] = `Bearer ${token}`;
		}

		return headers;
	}

	/**
	 * Handle API response
	 */
	private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
		const status = response.status;
		const contentType = response.headers.get("content-type");

		if (!contentType?.includes("application/json")) {
			return {
				status,
				error: { message: "Invalid response format" },
			};
		}

		const data = await response.json();

		if (status >= 200 && status < 300) {
			return { data, status };
		}

		return {
			status,
			data, // pass full body so unwrapData can read details
			error: {
				message: data.error || data.message || "An error occurred",
				code: data.code,
				field: data.field,
			},
		};
	}

	/**
	 * Attempt to refresh access token
	 * Returns: new token if successful, null if no refresh token or refresh failed
	 * NOTE: Does NOT redirect to login - let caller decide based on context
	 * 
	 * IMPORTANT: Uses a singleton pattern via refreshPromise to prevent race conditions
	 * when multiple requests get 401 simultaneously (e.g., 3 parallel AI tour-planning calls).
	 * Without this deduplication, parallel requests can all trigger independent refresh attempts,
	 * and if the refresh endpoint invalidates old tokens, the second+ attempts fail →
	 * clearTokens() → redirect to login.
	 */
	private async refreshAccessToken(): Promise<string | null> {
		// If a refresh is already in progress, join it instead of duplicating
		if (this.refreshPromise) {
			return this.refreshPromise;
		}

		this.refreshPromise = this._doRefreshAccessToken();
		const result = await this.refreshPromise;
		this.refreshPromise = null;
		return result;
	}

	private async _doRefreshAccessToken(): Promise<string | null> {
		const refreshToken = tokenStorage.getRefreshToken();

		// If no refresh token, just return null - let caller handle it
		// This allows optionalAuth routes to fall back to mock data
		if (!refreshToken) {
			return null;
		}

		try {
			const response = await fetch(`${this.baseUrl}/auth/refresh`, {
				method: "POST",
				headers: this.getHeaders(),
				body: JSON.stringify({ refreshToken }),
			});

			if (response.ok) {
				const data = await response.json();
				tokenStorage.setAccessToken(data.accessToken);
				if (data.refreshToken) {
					tokenStorage.setRefreshToken(data.refreshToken);
				}
				return data.accessToken;
			}

			// Refresh failed - tokens are invalid, clear them
			console.error('[Auth] Token refresh failed (response not ok)');
			tokenStorage.clearTokens();
			return null;
		} catch (err) {
			// Network error or parsing error
			console.error('[Auth] Token refresh failed with exception:', err);
			tokenStorage.clearTokens();
			return null;
		}
	}

	/**
	 * Make authenticated request with automatic token refresh
	 */
	private async request<T>(
		method: string,
		endpoint: string,
		options: RequestInit = {}
	): Promise<ApiResponse<T>> {
		const url = `${this.baseUrl}${endpoint}`;
		let token = tokenStorage.getAccessToken();

		const makeRequest = async (authToken: string | null): Promise<ApiResponse<T>> => {
			const headers: Record<string, string> = {
				...this.getHeaders(),
				...(options.headers as Record<string, string> | undefined),
			};

			if (authToken) {
				headers["Authorization"] = `Bearer ${authToken}`;
			}

			const response = await fetch(url, {
				method,
				headers,
				...options,
			});

			// Handle 401 - try to refresh token once
			if (response.status === 401) {
				console.warn('[Auth] Received 401 for', method, url);
				if (options.body?.toString().includes("refreshToken")) {
					console.warn('[Auth] Refresh endpoint itself returned 401');
				}
			}
			if (response.status === 401 && authToken && !options.body?.toString().includes("refreshToken")) {
				const newToken = await this.refreshAccessToken();
				if (newToken) {
					return makeRequest(newToken);
				}
			}

			return this.handleResponse<T>(response);
		};

		return makeRequest(token);
	}

	/**
	 * GET request
	 */
	async get<T>(
		endpoint: string,
		params?: Record<string, string | number | boolean | undefined>
	): Promise<ApiResponse<T>> {
		let url = endpoint;

		if (params) {
			const searchParams = new URLSearchParams();
			Object.entries(params).forEach(([key, value]) => {
				if (value !== undefined && value !== null) {
					searchParams.append(key, String(value));
				}
			});
			const queryString = searchParams.toString();
			if (queryString) {
				url += `?${queryString}`;
			}
		}

		return this.request<T>("GET", url);
	}

	/**
	 * POST request
	 */
	async post<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
		return this.request<T>("POST", endpoint, {
			body: data ? JSON.stringify(data) : undefined,
		});
	}

	/**
	 * PUT request
	 */
	async put<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
		return this.request<T>("PUT", endpoint, {
			body: data ? JSON.stringify(data) : undefined,
		});
	}

	/**
	 * PATCH request
	 */
	async patch<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
		return this.request<T>("PATCH", endpoint, {
			body: data ? JSON.stringify(data) : undefined,
		});
	}

	/**
	 * DELETE request
	 */
	async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
		return this.request<T>("DELETE", endpoint);
	}

	/**
	 * Upload file(s)
	 */
	async upload<T>(
		endpoint: string,
		files: File | File[],
		fields?: Record<string, string>
	): Promise<ApiResponse<T>> {
		const formData = new FormData();

		if (Array.isArray(files)) {
			files.forEach((file, index) => {
				formData.append(`file${index}`, file);
			});
		} else {
			formData.append("file", files);
		}

		if (fields) {
			Object.entries(fields).forEach(([key, value]) => {
				formData.append(key, value);
			});
		}

		const token = tokenStorage.getAccessToken();
		const headers: Record<string, string> = {};
		if (token) {
			headers["Authorization"] = `Bearer ${token}`;
		}

		const response = await fetch(`${this.baseUrl}${endpoint}`, {
			method: "POST",
			headers,
			body: formData,
		});

		return this.handleResponse<T>(response);
	}
}

// Export singleton instance
export const api = new ApiClient();

// =====================
// Ref-Files API Wrappers
// =====================

/**
 * Simple wrapper for apiGet calls from ref-files components
 * Handles both wrapped and unwrapped API responses
 */
export async function apiGet<T = any>(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>
): Promise<{ success: boolean; data?: T; error?: any }> {
  try {
    return unwrapData<T>(await api.get(endpoint, params));
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

function unwrapData<T>(response: { data?: unknown; status?: number; error?: any }): { success: boolean; data?: T; error?: any } {
  // If the response body itself is an error (status >= 400, body has success:false)
  // Check this FIRST so we get the full error body with details
  if (response.data && typeof response.data === 'object') {
    const body = response.data as any;
    if (body.success === false || (response.status && response.status >= 400)) {
      return { success: false, error: body };
    }
    // Standard wrapped response: { success: true, data: ... }
    if ('data' in body) {
      return { success: true, data: body.data as T | undefined };
    }
  }
  // If the response has a top-level error, propagate it
  if (response.error) {
    return { success: false, error: response.error };
  }
  // Fallback: pass through
  return { success: true, data: response.data as T | undefined };
}

/**
 * Simple wrapper for apiPost calls from ref-files components
 */
export async function apiPost<T = any>(
  endpoint: string,
  data?: unknown
): Promise<{ success: boolean; data?: T; error?: any }> {
  try {
    return unwrapData<T>(await api.post(endpoint, data));
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Simple wrapper for apiPut calls from ref-files components
 */
export async function apiPut<T = any>(
  endpoint: string,
  data?: unknown
): Promise<{ success: boolean; data?: T; error?: any }> {
  try {
    console.log('[apiPut] Request to', endpoint, 'with data:', data);
    const response = await api.put(endpoint, data);
    console.log('[apiPut] Response from', endpoint, ':', response);
    return unwrapData<T>(response);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Simple wrapper for apiPatch calls from ref-files components
 */
export async function apiPatch<T = any>(
   endpoint: string,
   data?: unknown
): Promise<{ success: boolean; data?: T; error?: any }> {
   try {
     return unwrapData<T>(await api.patch(endpoint, data));
   } catch (error) {
     return {
       success: false,
       error: error instanceof Error ? error.message : 'Unknown error',
     };
   }
}

/**
 * Simple wrapper for apiDelete calls from ref-files components
 */
export async function apiDelete<T = any>(
   endpoint: string
): Promise<{ success: boolean; data?: T; error?: any }> {
   try {
     return unwrapData<T>(await api.delete(endpoint));
   } catch (error) {
     return {
       success: false,
       error: error instanceof Error ? error.message : 'Unknown error',
     };
   }
}

/**
 * Decode a JWT payload without verification (frontend-side).
 * Returns null if the token is malformed.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
	try {
		const payload = token.split('.')[1];
		if (!payload) return null;
		return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
	} catch {
		return null;
	}
}

/**
 * Get a valid (non-expired) access token for use in stream URLs.
 *
 * Checks the stored access token's `exp` claim. If it's expired or will expire within
 * 30 seconds, attempts a refresh via the refresh token. Returns the valid token or null.
 *
 * Usage:
 *   const token = await getValidAccessToken();
 *   const streamUrl = `/api/music/${songId}/stream?token=${token}`;
 */
export async function getValidAccessToken(): Promise<string | null> {
	const token = tokenStorage.getAccessToken();
	if (!token) return null;

	// Decode JWT to check expiry
	const payload = decodeJwtPayload(token);
	if (payload?.exp && typeof payload.exp === 'number') {
		const expiresAt = payload.exp * 1000; // exp is in seconds, need ms
		// Refresh if expired or within 30 seconds of expiry
		if (Date.now() >= expiresAt - 30_000) {
			console.log('[Auth] Access token expired or near expiry, refreshing...');
			const refreshToken = tokenStorage.getRefreshToken();
			if (!refreshToken) return null;

			try {
				const response = await fetch('/api/auth/refresh', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ refreshToken }),
				});
				if (response.ok) {
					const data = await response.json();
					tokenStorage.setAccessToken(data.accessToken);
					if (data.refreshToken) {
						tokenStorage.setRefreshToken(data.refreshToken);
					}
					return data.accessToken;
				}
				// Refresh failed — clear tokens
				tokenStorage.clearTokens();
				return null;
			} catch (err) {
				console.error('[Auth] Token refresh failed:', err);
				return null;
			}
		}
	}

	return token;
}

// Export types for use in components
export type { ApiClient as ApiClientType };
